/**
 * Linked Variant Handler
 *
 * Substitutes "virtual" variant IDs with "real" variant IDs during cart addition.
 * Enables combined product listings without Shopify Plus.
 *
 * Requires:
 *   - Variant metafield: custom.gekoppelde_variant (variant reference)
 *   - Liquid snippet: linked-variant-handler.liquid (outputs mapping data)
 *
 * How it works:
 *   1. Snippet outputs window.linkedVariantMappings = { 'product-123': { 'virtualId': 'realId' } }
 *   2. This script wraps fetch() to intercept /cart/add.js requests
 *   3. If request body contains a virtual variant ID, it's swapped with the real ID
 *   4. Request proceeds normally with substituted ID
 *   5. Cart receives real variant, customer sees real product
 *
 * Compatible with:
 *   - Single product add-to-cart forms
 *   - Multi-variant selectors
 *   - Quick-buy buttons
 *   - Quantity > 1
 *   - Product addons
 *
 * Example:
 *   Virtual variant "60x64" [ID: 12345] → Real variant "Rooftrim 60x64 - Black" [ID: 48574]
 *   Customer adds virtual variant → Cart contains real variant
 */

(function () {
  'use strict';

  // Prevent double-initialization if script runs multiple times
  if (window.__linkedVariantsInitialized) return;

  // Store original fetch function with proper binding to preserve context
  const originalFetch = window.fetch.bind(window);

  /**
   * Find if a variant ID is a "virtual" linked variant
   * @param {string|number} variantId - Variant ID to check
   * @returns {{realId: string, handle: string}|null} Real variant ID + source handle if linked, null otherwise
   */
  function getRealVariantId(variantId) {
    const variantIdStr = String(variantId);

    // Validate ID is numeric
    if (!/^\d+$/.test(variantIdStr)) return null;

    // Check if we have any mappings
    if (!window.linkedVariantMappings) {
      return null;
    }

    // Check all product mappings
    for (const productKey in window.linkedVariantMappings) {
      if (Object.prototype.hasOwnProperty.call(window.linkedVariantMappings, productKey)) {
        const mapping = window.linkedVariantMappings[productKey];
        if (mapping[variantIdStr]) {
          return { realId: mapping[variantIdStr], handle: mapping._handle || '' };
        }
      }
    }

    return null;
  }

  /**
   * Process cart add payload and swap variant IDs if needed
   * @param {Object} data - Request body data
   * @returns {Object} Modified data with swapped IDs
   */
  function processCartAddData(data) {
    // Create a shallow copy to avoid mutating the original
    const processedData = { ...data };

    // Handle single item: {id: 123, quantity: 1, properties: {...}}
    if (processedData.id) {
      const match = getRealVariantId(processedData.id);
      if (match) {
        processedData.id = match.realId;
        if (match.handle) {
          processedData.properties = { ...processedData.properties, '_vervangen-voor': match.handle };
        }
      }
    }

    // Handle multiple items: {items: [{id: 123, quantity: 1}, ...]}
    if (processedData.items && Array.isArray(processedData.items)) {
      processedData.items = processedData.items.map((item) => {
        if (item.id) {
          const match = getRealVariantId(item.id);
          if (match) {
            const properties = match.handle
              ? { ...item.properties, '_vervangen-voor': match.handle }
              : item.properties;
            return { ...item, id: match.realId, properties };
          }
        }
        return item;
      });
    }

    return processedData;
  }

  /**
   * Wrapped fetch function that intercepts cart add requests
   * Uses Proxy pattern for better compatibility with other scripts
   */
  window.fetch = new Proxy(originalFetch, {
    apply(target, thisArg, args) {
      const [url, options] = args;

      // Check if this is a cart add request
      const isCartAddRequest =
        url && typeof url === 'string' && /\/cart\/add(?:\.js)?(?:\?|$)/.test(url);

      if (isCartAddRequest && options && options.body && typeof options.body === 'string') {
        try {
          // Parse request body
          const data = JSON.parse(options.body);

          // Process and swap variant IDs if needed
          const processedData = processCartAddData(data);

          // Only modify if something changed
          const wasSubstituted = JSON.stringify(data) !== JSON.stringify(processedData);
          if (wasSubstituted) {
            // Create new options object to avoid mutating original
            const modifiedOptions = {
              ...options,
              body: JSON.stringify(processedData),
            };
            args[1] = modifiedOptions;

            return Reflect.apply(target, thisArg, args).then((response) => {
              if (!response.ok) return Reflect.apply(target, thisArg, [url, options]);
              return response;
            });
          }
        } catch (e) {
          // If JSON parsing fails, let request proceed unchanged
        }
      }

      // Call original fetch with potentially modified arguments
      return Reflect.apply(target, thisArg, args);
    },
  });

  window.__linkedVariantsInitialized = true;
})();
